/**
 * CookieBanner.jsx — Banner de cookies minimalista (Cuadro Negro 2026-02-26).
 *
 * Aviso claro y profesional con dos acciones: Aceptar / Rechazar.
 * Persiste la elección en `localStorage.cookiesAccepted` ("true" | "false").
 * NO bloquea el render, NO usa overlay, NO usa inert, NO causa flash.
 * Se monta a nivel root en index.js, pero NO bloquea hydration.
 *
 * 🟦 2026-06-27 — i18n bilingüe ES/EN. NO usa useLanguage() porque
 * CookieBanner se monta a nivel root (index.js), FUERA del
 * <LanguageProvider> (que vive dentro de App.js). Por eso leemos el
 * idioma DIRECTO del localStorage / cookie / navigator.
 */
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'cookiesAccepted';
const LANGUAGE_LS_KEY = 'damyxia-language';
const LANGUAGE_COOKIE_NAME = 'damyxia_lang';

/** Detecta el idioma actual sin depender de React context. */
function detectLanguage() {
  try {
    // 1. localStorage (set por LanguageProvider).
    const ls = localStorage.getItem(LANGUAGE_LS_KEY);
    if (ls === 'es' || ls === 'en') return ls;
    // 2. cookie firmada del backend.
    const m = document.cookie
      .split('; ')
      .find((r) => r.startsWith(LANGUAGE_COOKIE_NAME + '='));
    if (m) {
      const v = decodeURIComponent(m.split('=')[1] || '');
      if (v === 'es' || v === 'en') return v;
    }
    // 3. navegador.
    const nav = (navigator.language || 'es').toLowerCase();
    if (nav.startsWith('en')) return 'en';
  } catch { /* ignore */ }
  return 'es';
}

export default function CookieBanner() {
  // Inicialmente invisible para evitar flash en SSR/hydration; en `useEffect`
  // decidimos si mostrarlo según el valor en localStorage.
  const [visible, setVisible] = useState(false);
  const [lang, setLang] = useState('es');

  useEffect(() => {
    setLang(detectLanguage());
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (choice !== 'true' && choice !== 'false') {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const acceptCookies = () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  const rejectCookies = () => {
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;
  const es = lang === 'es';

  return (
    <div className="cookie-banner" data-testid="cookie-banner" role="region" aria-label="Cookie consent">
      <p className="cookie-text">
        {es ? 'Este sitio utiliza' : 'This site uses'}{' '}
        <a
          href="/privacidad"
          className="cookie-link"
          title={es ? 'Leer política de privacidad' : 'Read privacy policy'}
          data-testid="cookie-link-privacidad"
        >
          cookies
        </a>
        {' '}
        {es
          ? 'para mejorar tu experiencia. Más información en nuestra política de privacidad.'
          : 'to improve your experience. More information in our privacy policy.'}
      </p>
      <div className="cookie-actions">
        <button
          type="button"
          onClick={acceptCookies}
          data-testid="cookie-accept-btn"
        >
          {es ? 'Aceptar' : 'Accept'}
        </button>
        <button
          type="button"
          onClick={rejectCookies}
          data-testid="cookie-reject-btn"
        >
          {es ? 'Rechazar' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
